import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import useBreakpoint from '../hooks/useBreakpoint';

const T = {
  ink:    '#111',
  muted:  '#888',
  border: '#e5e5e5',
  bg:     '#f7f7f7',
  font:   "'Helvetica Neue', Helvetica, Arial, sans-serif",
};

const inputSt = {
  width:'100%', border:`1.5px solid ${T.border}`, borderRadius:8,
  padding:'10px 12px', fontSize:13, boxSizing:'border-box',
  fontFamily:T.font, color:T.ink, background:'#f9fafb', outline:'none',
};
const labelSt = { display:'block', fontSize:12, fontWeight:600, color:T.muted, marginBottom:5 };

const ANREDEN    = ['Herr','Frau','Divers','Dr.','Prof.'];
const KONTAKTART = ['Mieter','Eigentümer','Verwalter','Dienstleister','Makler','Sonstiges'];
const UNTERNEHMENSART = ['GmbH','GbR','AG','OHG','KG','Einzelunternehmen','Sonstiges'];
const LAENDER = ['Deutschland','Österreich','Schweiz','Luxemburg','Andere'];
const PAGE_SIZE = 20;

function displayName(k) {
  if (k.is_sammelkontakt) return 'Sammelkunde/Sammellieferant';
  if (k.typ === 'unternehmen') return k.firmenname || '–';
  return [k.vorname, k.nachname].filter(Boolean).join(' ') || '–';
}

function Section({ title, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div style={{ marginBottom:8 }}>
      <div onClick={() => setOpen(o=>!o)} style={{ display:'flex',justifyContent:'space-between',alignItems:'center',padding:'12px 0',cursor:'pointer',borderBottom:`1px solid ${T.border}` }}>
        <span style={{ fontSize:16,fontWeight:800,color:T.ink }}>{title}</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2" strokeLinecap="round">
          {open ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
        </svg>
      </div>
      {open && <div style={{ paddingTop:16 }}>{children}</div>}
    </div>
  );
}

function Row2({ children, isMobile }) { return <div style={{ display:'grid',gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',gap:14,marginBottom:14 }}>{children}</div>; }
function F({ label, children }) { return <div><label style={labelSt}>{label}</label>{children}</div>; }
function Inp({ value, onChange, placeholder, type='text', readOnly }) {
  return (
    <input type={type} value={value||''} onChange={onChange} placeholder={placeholder||''} readOnly={readOnly}
      style={{ ...inputSt, background: readOnly?'#f3f4f6':inputSt.background }}
      onFocus={e=>{if(!readOnly)e.target.style.borderColor=T.ink}}
      onBlur={e=>e.target.style.borderColor=T.border}
    />
  );
}
function Sel({ value, onChange, options, placeholder }) {
  return (
    <div style={{ position:'relative' }}>
      <select value={value||''} onChange={onChange}
        style={{ ...inputSt, appearance:'none', WebkitAppearance:'none', cursor:'pointer', paddingRight:32 }}>
        {placeholder && <option value="">{placeholder}</option>}
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      <svg style={{ position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',pointerEvents:'none' }} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
    </div>
  );
}
function Toggle({ val, onChange, label }) {
  return (
    <div style={{ display:'flex',alignItems:'center',gap:12,margin:'16px 0' }}>
      <div onClick={() => onChange(!val)} style={{ width:44,height:26,borderRadius:13,background:val?T.ink:'#d1d5db',cursor:'pointer',position:'relative',transition:'background 0.2s',flexShrink:0 }}>
        <div style={{ position:'absolute',top:3,left:val?19:3,width:20,height:20,borderRadius:'50%',background:'white',transition:'left 0.2s',boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }}/>
      </div>
      <span style={{ fontSize:13,color:T.ink }}>{label}</span>
    </div>
  );
}

/* ══════════════════════════════════════════════
   Create / Edit Modal
══════════════════════════════════════════════ */
const EMPTY = { typ:'privatperson', anrede:'', vorname:'', nachname:'', kontaktart:'', geburtsdatum:'', unternehmensart:'', firmenname:'', kreditor_id:'', debitor_id:'', aktiv:true, rolle:'', strasse:'', hausnummer:'', plz:'', stadt:'', land:'', email:'', festnetz:'', handynummer:'', kontoinhaber:'', bank:'', iban:'', bic:'', steuernummer:'', ust_idnr:'', glaeubiger_id:'', notiz:'' };

function KontaktModal({ editing, onSave, onClose }) {
  const { isMobile } = useBreakpoint();
  const [form, setForm] = useState(editing ? { ...EMPTY, ...editing } : EMPTY);
  const [erweitert, setErweitert] = useState(true);
  const [saving, setSaving] = useState(false);
  function set(k,v) { setForm(f=>({...f,[k]:v})); }
  const ready = form.typ === 'unternehmen' ? !!form.firmenname?.trim() : !!form.nachname?.trim();

  async function handleSubmit() {
    if (!ready) return;
    setSaving(true);
    await onSave(form, editing?.id);
    setSaving(false);
  }

  return (
    <div onClick={e=>{if(e.target===e.currentTarget)onClose();}} style={{ position:'fixed',inset:0,zIndex:200,background:'rgba(17,24,39,0.5)',backdropFilter:'blur(3px)',display:'flex',alignItems:'flex-start',justifyContent:'center',padding:'20px',overflowY:'auto' }}>
      <div style={{ background:'white',borderRadius:16,width:'100%',maxWidth:680,boxShadow:'0 24px 64px rgba(0,0,0,0.18)',margin:'auto 0' }}>
        {/* Header */}
        <div style={{ padding:'22px 28px 0',textAlign:'center' }}>
          <h2 style={{ margin:'0 0 20px',fontSize:18,fontWeight:800,color:T.ink }}>Lege einen neuen Kontakt an</h2>
          {/* Tabs */}
          <div style={{ display:'flex',gap:0,borderBottom:`2px solid ${T.border}` }}>
            {['privatperson','unternehmen'].map(tab => (
              <button key={tab} onClick={()=>set('typ',tab)} style={{ flex:1,padding:'10px',border:'none',background:'none',cursor:'pointer',fontSize:14,fontWeight:700,color:form.typ===tab?T.ink:T.muted,borderBottom:`2px solid ${form.typ===tab?T.ink:'transparent'}`,marginBottom:-2,fontFamily:T.font,transition:'color 0.15s' }}>
                {tab === 'privatperson' ? 'Privatperson' : 'Unternehmen'}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding:'0 28px 8px',maxHeight:'70vh',overflowY:'auto' }}>
          {form.typ === 'privatperson' ? (
            <>
              <Section title="Kontakt">
                <Row2 isMobile={isMobile}>
                  <F label="Anrede"><Sel value={form.anrede} onChange={e=>set('anrede',e.target.value)} options={ANREDEN} placeholder="Wähle eine Option"/></F>
                  <F label="Vorname"><Inp value={form.vorname} onChange={e=>set('vorname',e.target.value)}/></F>
                </Row2>
                <F label="Nachname *" ><Inp value={form.nachname} onChange={e=>set('nachname',e.target.value)}/></F>
                <div style={{ marginTop:14 }}>
                  <Row2 isMobile={isMobile}>
                    <F label="Kontaktart"><Sel value={form.kontaktart} onChange={e=>set('kontaktart',e.target.value)} options={KONTAKTART} placeholder="Wähle eine Option"/></F>
                    <F label="Geburtsdatum">
                      <div style={{ position:'relative' }}>
                        <Inp type="date" value={form.geburtsdatum} onChange={e=>set('geburtsdatum',e.target.value)}/>
                        <svg style={{ position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',pointerEvents:'none' }} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                      </div>
                    </F>
                  </Row2>
                  <Row2 isMobile={isMobile}>
                    <F label="Kreditor-ID"><Inp value={form.kreditor_id} onChange={e=>set('kreditor_id',e.target.value)} placeholder="Wird automatisch vergeben"/></F>
                    <F label="Debitor-ID"><Inp value={form.debitor_id} onChange={e=>set('debitor_id',e.target.value)} placeholder="Wird automatisch vergeben"/></F>
                  </Row2>
                </div>
              </Section>
              <Toggle val={erweitert} onChange={setErweitert} label="Erweiterte Kontaktinformationen einblenden?"/>
            </>
          ) : (
            <>
              <Section title="Unternehmensinformationen">
                <Row2 isMobile={isMobile}>
                  <F label="Unternehmensart"><Sel value={form.unternehmensart} onChange={e=>set('unternehmensart',e.target.value)} options={UNTERNEHMENSART} placeholder="Wähle eine Option"/></F>
                  <F label="Firmenname *"><Inp value={form.firmenname} onChange={e=>set('firmenname',e.target.value)}/></F>
                </Row2>
                <Row2 isMobile={isMobile}>
                  <F label="Kreditor-ID"><Inp value={form.kreditor_id} onChange={e=>set('kreditor_id',e.target.value)} placeholder="Wird automatisch vergeben"/></F>
                  <F label="Debitor-ID"><Inp value={form.debitor_id} onChange={e=>set('debitor_id',e.target.value)} placeholder="Wird automatisch vergeben"/></F>
                </Row2>
              </Section>
              <Toggle val={erweitert} onChange={setErweitert} label="Erweiterte Kontaktinformationen einblenden?"/>
            </>
          )}

          <Section title="Adresse">
            <Row2 isMobile={isMobile}>
              <F label="Strasse"><Inp value={form.strasse} onChange={e=>set('strasse',e.target.value)}/></F>
              <F label="Hausnummer"><Inp value={form.hausnummer} onChange={e=>set('hausnummer',e.target.value)}/></F>
            </Row2>
            <Row2 isMobile={isMobile}>
              <F label="PLZ"><Inp value={form.plz} onChange={e=>set('plz',e.target.value)}/></F>
              <F label="Stadt"><Inp value={form.stadt} onChange={e=>set('stadt',e.target.value)}/></F>
            </Row2>
            <F label="Land"><Sel value={form.land} onChange={e=>set('land',e.target.value)} options={LAENDER} placeholder="Wähle eine Option"/></F>
          </Section>

          <Section title="Kontaktmöglichkeiten">
            <F label="E-Mail"><Inp type="email" value={form.email} onChange={e=>set('email',e.target.value)}/></F>
            <div style={{ marginTop:14 }}>
              <Row2 isMobile={isMobile}>
                <F label="Festnetz"><Inp value={form.festnetz} onChange={e=>set('festnetz',e.target.value)}/></F>
                <F label="Handynummer"><Inp value={form.handynummer} onChange={e=>set('handynummer',e.target.value)}/></F>
              </Row2>
            </div>
          </Section>

          <Section title="Bankdaten">
            <Row2 isMobile={isMobile}>
              <F label="Kontoinhaber"><Inp value={form.kontoinhaber} onChange={e=>set('kontoinhaber',e.target.value)}/></F>
              <F label="Bank"><Inp value={form.bank} onChange={e=>set('bank',e.target.value)}/></F>
            </Row2>
            <Row2 isMobile={isMobile}>
              <F label="IBAN"><Inp value={form.iban} onChange={e=>set('iban',e.target.value)}/></F>
              <F label="BIC"><Inp value={form.bic} onChange={e=>set('bic',e.target.value)}/></F>
            </Row2>
            <Row2 isMobile={isMobile}>
              <F label="Steuernummer"><Inp value={form.steuernummer} onChange={e=>set('steuernummer',e.target.value)}/></F>
              <F label="USt-IdNr."><Inp value={form.ust_idnr} onChange={e=>set('ust_idnr',e.target.value)}/></F>
            </Row2>
            <F label="Gläubiger-ID"><Inp value={form.glaeubiger_id} onChange={e=>set('glaeubiger_id',e.target.value)}/></F>
          </Section>
        </div>

        {/* Footer */}
        <div style={{ padding:'16px 28px',borderTop:`1px solid ${T.border}`,display:'flex',justifyContent:'flex-end',gap:12 }}>
          <button onClick={onClose} style={{ padding:'10px 20px',borderRadius:9,border:'none',background:'none',fontSize:14,color:T.muted,cursor:'pointer',fontFamily:T.font,fontWeight:700 }}>Abbrechen</button>
          <button onClick={handleSubmit} disabled={!ready||saving}
            style={{ padding:'10px 28px',borderRadius:9,border:'none',background:ready?T.ink:'#d1d5db',color:'white',fontSize:14,fontWeight:700,cursor:ready?'pointer':'not-allowed',fontFamily:T.font }}>
            {saving ? 'Wird erstellt…' : editing ? 'Änderungen speichern' : 'Kontakt erstellen'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   Detail Panel (slide-in from right)
══════════════════════════════════════════════ */
function DetailPanel({ kontakt: k, onClose, onEdit, onNoteAdd }) {
  const [tab, setTab] = useState('kontakt');
  const [note, setNote] = useState('');
  const name = displayName(k);

  function InfoRow({ label, value }) {
    return (
      <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:8 }}>
        <div style={{ fontSize:12,color:T.muted }}>{label}</div>
        <div style={{ fontSize:13,color:T.ink }}>{value || '–'}</div>
      </div>
    );
  }

  function SectionHead({ title, onChangeCb }) {
    return (
      <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12,marginTop:20 }}>
        <span style={{ fontSize:14,fontWeight:700,color:T.ink }}>{title}</span>
        {onChangeCb && <button onClick={onChangeCb} style={{ background:'none',border:'none',fontSize:12,color:T.ink,cursor:'pointer',fontWeight:700,fontFamily:T.font }}>Ändern</button>}
      </div>
    );
  }

  return (
    <div style={{ position:'fixed',inset:0,zIndex:150,display:'flex' }}>
      <div onClick={onClose} style={{ flex:1,background:'rgba(17,24,39,0.3)' }}/>
      <div style={{ width:720,background:'white',height:'100%',overflowY:'auto',boxShadow:'-24px 0 48px rgba(0,0,0,0.12)',display:'flex',flexDirection:'column',fontFamily:T.font }}>
        {/* Back */}
        <div style={{ padding:'16px 28px',borderBottom:`1px solid ${T.border}` }}>
          <button onClick={onClose} style={{ display:'flex',alignItems:'center',gap:8,background:'none',border:'none',cursor:'pointer',fontSize:13,color:T.ink,fontFamily:T.font,fontWeight:600 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
            Details schließen
          </button>
        </div>

        <div style={{ flex:1,padding:'24px 28px',overflowY:'auto' }}>
          {/* Header row */}
          <div style={{ display:'flex',alignItems:'center',gap:16,marginBottom:16,flexWrap:'wrap' }}>
            <div style={{ width:48,height:48,borderRadius:'50%',background:'#0d9488',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            </div>
            <div style={{ flex:1 }}>
              <div style={{ display:'flex',alignItems:'center',gap:10,flexWrap:'wrap' }}>
                <span style={{ fontSize:18,fontWeight:800,color:T.ink }}>{name}</span>
                {k.is_sammelkontakt && <span style={{ fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:20,background:'#374151',color:'white' }}>Sammelkontakt</span>}
                {k.rolle && !k.is_sammelkontakt && <span style={{ fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:20,background:'#f5f5f5',color:T.ink }}>{k.rolle}</span>}
              </div>
            </div>
            <div style={{ display:'flex',gap:8 }}>
              <button onClick={onEdit} style={{ padding:'8px 16px',borderRadius:8,border:`1.5px solid ${T.border}`,background:'white',fontSize:13,color:T.ink,cursor:'pointer',fontFamily:T.font,fontWeight:600 }}>Bearbeiten</button>
              <button style={{ padding:'8px 16px',borderRadius:8,border:'none',background:T.ink,color:'white',fontSize:13,cursor:'pointer',fontFamily:T.font,fontWeight:700 }}>Nachricht senden</button>
            </div>
          </div>

          {/* Info banner for Sammelkontakt */}
          {k.is_sammelkontakt && (
            <div style={{ display:'flex',alignItems:'flex-start',gap:10,padding:'12px 14px',background:'#f0f9ff',borderRadius:8,marginBottom:16,border:'1px solid #bae6fd' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2" strokeLinecap="round" style={{ flexShrink:0,marginTop:1 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              <span style={{ fontSize:12,color:'#0c4a6e',lineHeight:1.55 }}>Verwende den Sammelkontakt für Kunden- oder Lieferanten, für die Du nur selten Buchungen durchführst.</span>
            </div>
          )}

          {/* Tabs */}
          <div style={{ display:'flex',borderBottom:`2px solid ${T.border}`,marginBottom:20 }}>
            {[{key:'kontakt',label:'Kontaktdetails'},{key:'finanzen',label:'Finanzdetails'}].map(t=>(
              <button key={t.key} onClick={()=>setTab(t.key)} style={{ padding:'10px 0',marginRight:24,border:'none',background:'none',fontSize:14,fontWeight:700,color:tab===t.key?T.ink:T.muted,borderBottom:`2px solid ${tab===t.key?T.ink:'transparent'}`,marginBottom:-2,cursor:'pointer',fontFamily:T.font }}>
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'kontakt' && (
            <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:32 }}>
              {/* Left */}
              <div>
                <SectionHead title="Kontaktmöglichkeiten" onChangeCb={onEdit}/>
                <div style={{ borderTop:`1px solid ${T.border}`,paddingTop:12 }}>
                  <InfoRow label="E-Mail Adresse" value={k.email}/>
                  <InfoRow label="Telefon" value={k.festnetz}/>
                  <InfoRow label="Mobiltelefon" value={k.handynummer}/>
                </div>

                <SectionHead title="Adresse"/>
                <div style={{ borderTop:`1px solid ${T.border}`,paddingTop:12 }}>
                  <div style={{ fontSize:13,color:T.ink,marginBottom:4 }}>{name}</div>
                  {(k.strasse||k.plz||k.stadt) && <div style={{ fontSize:12,color:T.muted }}>{[k.strasse,k.hausnummer].filter(Boolean).join(' ')}{k.plz||k.stadt?`, ${[k.plz,k.stadt].filter(Boolean).join(' ')}`:''}</div>}
                </div>

                <SectionHead title="Bankverbindung" onChangeCb={onEdit}/>
                <div style={{ borderTop:`1px solid ${T.border}`,paddingTop:12 }}>
                  <InfoRow label="Kontoinhaber" value={k.kontoinhaber}/>
                  <InfoRow label="Bank" value={k.bank}/>
                  <InfoRow label="IBAN" value={k.iban}/>
                  <InfoRow label="BIC" value={k.bic}/>
                  <InfoRow label="Gläubiger-ID" value={k.glaeubiger_id}/>
                </div>
              </div>

              {/* Right */}
              <div>
                <SectionHead title="Sonstiges" onChangeCb={onEdit}/>
                <div style={{ borderTop:`1px solid ${T.border}`,paddingTop:12 }}>
                  <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:8 }}>
                    <div>
                      <div style={{ fontSize:11,color:T.muted,marginBottom:2 }}>Kreditor-ID</div>
                      <div style={{ fontSize:13,fontWeight:700,color:T.ink }}>{k.kreditor_id||'–'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize:11,color:T.muted,marginBottom:2 }}>Debitor-ID</div>
                      <div style={{ fontSize:13,fontWeight:700,color:T.ink }}>{k.debitor_id||'–'}</div>
                    </div>
                  </div>
                </div>

                <SectionHead title="Notizen" onChangeCb={onEdit}/>
                <div style={{ borderTop:`1px solid ${T.border}`,paddingTop:12 }}>
                  {k.notiz && <div style={{ fontSize:13,color:T.ink,marginBottom:12,lineHeight:1.6 }}>{k.notiz}</div>}
                  <div style={{ display:'flex',gap:8 }}>
                    <input value={note} onChange={e=>setNote(e.target.value)} placeholder="Notiz hinzufügen"
                      style={{ ...inputSt,flex:1 }}
                      onFocus={e=>e.target.style.borderColor=T.ink} onBlur={e=>e.target.style.borderColor=T.border}
                    />
                    <button onClick={()=>{onNoteAdd(note);setNote('');}} disabled={!note.trim()} style={{ padding:'9px 16px',borderRadius:8,border:'none',background:note.trim()?T.ink:'#d1d5db',color:'white',fontSize:13,cursor:note.trim()?'pointer':'not-allowed',fontFamily:T.font,fontWeight:700,whiteSpace:'nowrap' }}>Hinzufügen</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {tab === 'finanzen' && (
            <div style={{ padding:'20px 0',textAlign:'center',color:T.muted,fontSize:13 }}>
              Finanzdetails — demnächst verfügbar.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   Main Kontakte Page
══════════════════════════════════════════════ */
export default function Kontakte() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const { isMobile } = useBreakpoint();

  const [kontakte, setKontakte] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [detail, setDetail]     = useState(null);
  const [page, setPage]         = useState(1);

  const [search, setSearch]         = useState('');
  const [filterTyp, setFilterTyp]   = useState('');
  const [filterRolle, setFilterRolle] = useState('');
  const [filterAktiv, setFilterAktiv] = useState(true);

  useEffect(() => { fetchAll(); }, []);

  async function fetchAll() {
    const { data } = await supabase.from('kontakte').select('*').eq('owner_id', user.id).order('created_at', { ascending: false });
    setKontakte(data || []);
    setLoading(false);
  }

  async function ensureSammelkontakt(data) {
    if (data.length === 0) {
      await supabase.from('kontakte').insert({ owner_id: user.id, typ:'privatperson', is_sammelkontakt:true, kreditor_id:'70000', debitor_id:'10000' });
      await fetchAll();
    }
  }

  useEffect(() => { if (!loading) ensureSammelkontakt(kontakte); }, [loading]);

  async function handleSave(form, id) {
    const payload = { ...form, owner_id: user.id };
    const { error } = id
      ? await supabase.from('kontakte').update(payload).eq('id', id).eq('owner_id', user.id)
      : await supabase.from('kontakte').insert(payload);
    if (error) { addToast('Fehler: ' + error.message, 'error'); return; }
    await fetchAll();
    setShowCreate(false);
    setEditing(null);
    if (detail) setDetail(d => kontakte.find(k=>k.id===d?.id) || d);
    addToast(id ? 'Kontakt aktualisiert.' : 'Kontakt erstellt.', 'success');
  }

  async function handleNoteAdd(kontaktId, note) {
    if (!note.trim()) return;
    const k = kontakte.find(x=>x.id===kontaktId);
    const newNote = k?.notiz ? k.notiz + '\n' + note : note;
    await supabase.from('kontakte').update({ notiz: newNote }).eq('id', kontaktId);
    await fetchAll();
    addToast('Notiz gespeichert.', 'success');
  }

  function exportCSV() {
    const header = ['Name','Typ','Rolle','E-Mail','Telefon','Straße','PLZ','Stadt','Kreditor-ID','Debitor-ID'];
    const rows = filtered.map(k => [displayName(k), k.typ, k.rolle||'', k.email||'', k.festnetz||'', k.strasse||'', k.plz||'', k.stadt||'', k.kreditor_id||'', k.debitor_id||'']);
    const csv = [header,...rows].map(r=>r.map(v=>`"${String(v??'').replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF'+csv], {type:'text/csv;charset=utf-8;'});
    const a = document.createElement('a'); a.href=URL.createObjectURL(blob); a.download='kontakte.csv'; a.click();
    addToast('Kontakte exportiert.', 'success');
  }

  const hasFilter = search || filterTyp || filterRolle;
  const filtered = kontakte.filter(k => {
    const q = search.toLowerCase();
    const matchQ = !q || displayName(k).toLowerCase().includes(q) || (k.email||'').toLowerCase().includes(q);
    const matchT = !filterTyp || k.typ === filterTyp;
    const matchR = !filterRolle || k.rolle === filterRolle;
    const matchA = !filterAktiv || k.aktiv;
    return matchQ && matchT && matchR && matchA;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE);
  const start = filtered.length === 0 ? 0 : (page-1)*PAGE_SIZE+1;
  const end   = Math.min(page*PAGE_SIZE, filtered.length);

  const allRollen = [...new Set(kontakte.map(k=>k.rolle).filter(Boolean))];

  return (
    <div style={{ maxWidth:1200, color:T.ink, fontFamily:T.font }}>

      {(showCreate || editing) && <KontaktModal editing={editing} onSave={handleSave} onClose={()=>{setShowCreate(false);setEditing(null);}}/>}
      {detail && (
        <DetailPanel
          kontakt={detail}
          onClose={()=>setDetail(null)}
          onEdit={()=>{setEditing(detail);setDetail(null);}}
          onNoteAdd={(note)=>handleNoteAdd(detail.id, note)}
        />
      )}

      {/* ── Header ── */}
      <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:28,gap:12,flexWrap:'wrap' }}>
        <h1 style={{ margin:0,fontSize:isMobile?22:28,fontWeight:800,letterSpacing:'-0.6px',color:T.ink }}>Kontakte</h1>
        <div style={{ display:'flex',gap:10 }}>
          <button onClick={exportCSV} style={{ display:'flex',alignItems:'center',gap:7,padding:'9px 18px',borderRadius:9,border:`1.5px solid ${T.border}`,background:'white',fontSize:13,color:T.ink,cursor:'pointer',fontFamily:T.font,fontWeight:600 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Exportieren
          </button>
          <button onClick={()=>setShowCreate(true)} style={{ display:'flex',alignItems:'center',gap:7,padding:'9px 18px',borderRadius:9,border:'none',background:T.ink,color:'white',fontSize:13,cursor:'pointer',fontFamily:T.font,fontWeight:700 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Kontakt hinzufügen
          </button>
        </div>
      </div>

      {/* ── Filter bar ── */}
      <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:20,flexWrap:'wrap' }}>
        <div style={{ position:'relative',flex:'1 1 200px',minWidth:160 }}>
          <svg style={{ position:'absolute',left:11,top:'50%',transform:'translateY(-50%)',pointerEvents:'none' }} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input type="text" placeholder="Kontakte durchsuchen..." value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}}
            style={{ width:'100%',border:`1.5px solid ${T.border}`,borderRadius:9,padding:'8px 12px 8px 32px',fontSize:13,boxSizing:'border-box',outline:'none',fontFamily:T.font,height:38 }}/>
        </div>

        <button style={{ display:'flex',alignItems:'center',gap:6,padding:'8px 14px',border:`1.5px solid ${T.border}`,borderRadius:9,background:'white',fontSize:13,color:T.ink,cursor:'pointer',fontFamily:T.font,fontWeight:600,height:38 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/></svg>
          Filter
        </button>

        {/* Typ chip */}
        <div style={{ position:'relative' }}>
          <select value={filterTyp} onChange={e=>{setFilterTyp(e.target.value);setPage(1);}}
            style={{ appearance:'none',WebkitAppearance:'none',background:filterTyp?'#f5f5f5':'white',border:`1.5px solid ${filterTyp?T.ink:T.border}`,borderRadius:20,padding:'6px 28px 6px 12px',fontSize:12,color:filterTyp?T.ink:T.ink,cursor:'pointer',fontWeight:filterTyp?700:400,outline:'none',height:34 }}>
            <option value="">Typ</option>
            <option value="privatperson">Privatperson</option>
            <option value="unternehmen">Unternehmen</option>
          </select>
          <svg style={{ position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',pointerEvents:'none' }} width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
        </div>

        {/* Rolle chip */}
        <div style={{ position:'relative' }}>
          <select value={filterRolle} onChange={e=>{setFilterRolle(e.target.value);setPage(1);}}
            style={{ appearance:'none',WebkitAppearance:'none',background:filterRolle?'#f5f5f5':'white',border:`1.5px solid ${filterRolle?T.ink:T.border}`,borderRadius:20,padding:'6px 28px 6px 12px',fontSize:12,color:filterRolle?T.ink:T.ink,cursor:'pointer',fontWeight:filterRolle?700:400,outline:'none',height:34 }}>
            <option value="">Rolle</option>
            {KONTAKTART.map(r=><option key={r} value={r}>{r}</option>)}
          </select>
          <svg style={{ position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',pointerEvents:'none' }} width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
        </div>

        {/* Nur aktive chip */}
        <button onClick={()=>setFilterAktiv(v=>!v)} style={{ display:'flex',alignItems:'center',gap:6,padding:'6px 14px',borderRadius:20,border:`1.5px solid ${filterAktiv?T.ink:T.border}`,background:filterAktiv?'#f5f5f5':'white',fontSize:12,color:filterAktiv?T.ink:T.ink,cursor:'pointer',fontFamily:T.font,fontWeight:filterAktiv?700:400 }}>
          Nur aktive
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
        </button>

        {(hasFilter||!filterAktiv) && (
          <button onClick={()=>{setSearch('');setFilterTyp('');setFilterRolle('');setFilterAktiv(true);setPage(1);}} style={{ background:'none',border:'none',fontSize:13,color:T.ink,cursor:'pointer',fontWeight:600,padding:'0 4px' }}>
            Filter zurücksetzen
          </button>
        )}
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div style={{ background:'white',border:`1px solid ${T.border}`,borderRadius:12,padding:24 }}>
          {[1,2,3].map(n=><div key={n} className="skeleton" style={{ height:52,borderRadius:8,marginBottom:10 }}/>)}
        </div>
      ) : (
        <div style={{ background:'white',border:`1px solid ${T.border}`,borderRadius:12,overflow:'hidden',boxShadow:'0 2px 8px rgba(0,0,0,0.04)' }}>
          {/* Column headers */}
          {!isMobile && (
          <div style={{ display:'grid',gridTemplateColumns:'3fr 1.5fr 1.5fr 1.5fr 40px',padding:'11px 20px',borderBottom:`1px solid ${T.border}`,background:T.bg }}>
            {['Kundenname','Adresse','Kontakt','Typ / Rolle',''].map(h=>(
              <span key={h} style={{ fontSize:11,fontWeight:700,letterSpacing:'0.5px',textTransform:'uppercase',color:'#9ca3af' }}>{h}</span>
            ))}
          </div>
          )}

          {paginated.length === 0 ? (
            <div style={{ overflow:'hidden' }}>
              {hasFilter ? (
                <div style={{ textAlign:'center', padding:'60px 24px' }}>
                  <div style={{ width:56,height:56,background:'#f7f7f7',borderRadius:14,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 16px' }}>
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  </div>
                  <div style={{ fontSize:16,fontWeight:700,color:T.ink,marginBottom:8 }}>Keine Kontakte gefunden</div>
                  <div style={{ fontSize:13,color:T.muted,lineHeight:1.7 }}>Versuche andere Filter oder setze die Suche zurück.</div>
                </div>
              ) : (
                <div style={{ padding:'48px 40px' }}>
                  <div style={{ display:'flex',alignItems:'flex-start',gap:32,marginBottom:40 }}>
                    <div style={{ flexShrink:0,width:64,height:64,background:'#f7f7f7',borderRadius:16,display:'flex',alignItems:'center',justifyContent:'center',border:`1.5px solid ${T.border}` }}>
                      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:20,fontWeight:700,color:T.ink,marginBottom:8,letterSpacing:'-0.3px' }}>Noch keine Kontakte vorhanden</div>
                      <div style={{ fontSize:14,color:T.muted,lineHeight:1.8,maxWidth:520 }}>Verwalte alle wichtigen Kontakte rund um deine Immobilien — Handwerker, Hausverwalter, Makler und mehr — zentral an einem Ort.</div>
                      <button onClick={()=>setShowCreate(true)} className="btn-primary" style={{ marginTop:20 }}>+ Ersten Kontakt anlegen</button>
                    </div>
                  </div>
                  <div style={{ display:'grid',gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)',gap:16 }}>
                    {[
                      { icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>, title:'Handwerker & Dienstleister', text:'Speichere Handwerker mit Fachgebiet und Kontaktdaten.' },
                      { icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>, title:'Hausverwaltungen', text:'Verknüpfe Kontakte direkt mit deinen Objekten.' },
                      { icon:<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.62 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.16 6.16l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>, title:'Schnell erreichbar', text:'Telefon, E-Mail und Adresse immer griffbereit.' },
                    ].map((f,i)=>(
                      <div key={i} style={{ background:'#f7f7f7',borderRadius:12,padding:'18px 20px',border:`1px solid ${T.border}` }}>
                        <div style={{ width:36,height:36,background:'white',borderRadius:9,display:'flex',alignItems:'center',justifyContent:'center',marginBottom:12,border:`1px solid ${T.border}`,boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>{f.icon}</div>
                        <div style={{ fontSize:13,fontWeight:700,color:T.ink,marginBottom:4 }}>{f.title}</div>
                        <div style={{ fontSize:12,color:T.muted,lineHeight:1.6 }}>{f.text}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : isMobile ? paginated.map((k,i) => {
            const name = displayName(k);
            return (
              <div key={k.id} onClick={()=>setDetail(k)}
                style={{ padding:'14px 18px', borderBottom:i<paginated.length-1?`1px solid ${T.border}`:'none', cursor:'pointer' }}>
                <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8 }}>
                  <div style={{ width:32,height:32,borderRadius:'50%',background:'#f3f4f6',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2" strokeLinecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex',alignItems:'center',gap:8,flexWrap:'wrap' }}>
                      <span style={{ fontSize:14,fontWeight:700,color:T.ink }}>{name}</span>
                      {k.is_sammelkontakt && <span style={{ fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:20,background:'#374152',color:'white' }}>Sammelkontakt</span>}
                    </div>
                    <div style={{ fontSize:12,color:T.muted,marginTop:2 }}>{k.typ==='privatperson'?'Privatperson':'Unternehmen'}</div>
                  </div>
                </div>
                {(k.email||k.festnetz||k.handynummer) && (
                  <div style={{ fontSize:12,color:T.muted,marginBottom:4 }}>
                    {k.email && <div>{k.email}</div>}
                    {(k.festnetz||k.handynummer) && <div>{k.festnetz||k.handynummer}</div>}
                  </div>
                )}
              </div>
            );
          }) : paginated.map((k,i) => {
            const name = displayName(k);
            return (
              <div key={k.id} onClick={()=>setDetail(k)}
                style={{ display:'grid',gridTemplateColumns:'3fr 1.5fr 1.5fr 1.5fr 40px',padding:'14px 20px',alignItems:'center',borderBottom:i<paginated.length-1?`1px solid ${T.border}`:'none',cursor:'pointer',transition:'background 0.1s' }}
                onMouseEnter={e=>e.currentTarget.style.background='#fafafa'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                {/* Name */}
                <div style={{ display:'flex',alignItems:'center',gap:12 }}>
                  <div style={{ width:32,height:32,borderRadius:'50%',background:'#f3f4f6',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2" strokeLinecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  </div>
                  <div>
                    <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                      <span style={{ fontSize:13,fontWeight:700,color:T.ink }}>{name}</span>
                      {k.is_sammelkontakt && <span style={{ fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:20,background:'#374151',color:'white' }}>Sammelkontakt</span>}
                    </div>
                    {(k.kreditor_id||k.debitor_id) && (
                      <div style={{ fontSize:11,color:T.muted,marginTop:2 }}>
                        {k.kreditor_id?`Kreditor-ID: ${k.kreditor_id}`:''}{k.kreditor_id&&k.debitor_id?' | ':''}{k.debitor_id?`Debitor-ID: ${k.debitor_id}`:''}
                      </div>
                    )}
                  </div>
                </div>
                {/* Adresse */}
                <div style={{ fontSize:12,color:T.muted }}>
                  {[k.strasse,k.hausnummer].filter(Boolean).join(' ')}{k.plz||k.stadt?`, ${[k.plz,k.stadt].filter(Boolean).join(' ')}`:''}
                  {!k.strasse&&!k.plz&&!k.stadt?'–':''}
                </div>
                {/* Kontakt */}
                <div style={{ fontSize:12,color:T.muted }}>{k.email||k.festnetz||k.handynummer||'–'}</div>
                {/* Typ/Rolle */}
                <div>
                  <div style={{ fontSize:12,color:T.muted }}>–</div>
                  <div style={{ fontSize:11,color:T.muted }}>{k.typ==='privatperson'?'Privatperson':'Unternehmen'}</div>
                </div>
                {/* 3-dot */}
                <div onClick={e=>e.stopPropagation()} style={{ display:'flex',justifyContent:'flex-end' }}>
                  <button style={{ background:'none',border:'none',cursor:'pointer',padding:4,color:T.muted,display:'flex' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/></svg>
                  </button>
                </div>
              </div>
            );
          })}

          {/* Pagination */}
          <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',padding:'12px 20px',borderTop:`1px solid ${T.border}`,background:T.bg }}>
            <div style={{ display:'flex',alignItems:'center',gap:8 }}>
              <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} style={{ width:28,height:28,borderRadius:6,border:`1px solid ${T.border}`,background:'white',cursor:page===1?'not-allowed':'pointer',display:'flex',alignItems:'center',justifyContent:'center',opacity:page===1?0.4:1 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
              <span style={{ fontSize:13,fontWeight:700,color:T.ink,width:24,textAlign:'center' }}>{page}</span>
              <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages} style={{ width:28,height:28,borderRadius:6,border:`1px solid ${T.border}`,background:'white',cursor:page===totalPages?'not-allowed':'pointer',display:'flex',alignItems:'center',justifyContent:'center',opacity:page===totalPages?0.4:1 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            </div>
            <span style={{ fontSize:12,color:T.muted }}>Zeige {start} bis {end} von {filtered.length} Einträgen</span>
          </div>
        </div>
      )}
    </div>
  );
}
